import { getSuarteEnvironment } from "./environment.utils";

const LIGHT_MODE = "light";
const DARK_MODE = "dark";
const THEME_CHANGE_EVENT = "THEME_CHANGE";
const THEME_EVENT_NAME = "suarte:native-theme-change";

function canAccessDOM() {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

export function getCurrentMode() {
  if (!canAccessDOM()) {
    return DARK_MODE;
  }

  const attr = document.documentElement.getAttribute("data-mode");
  if (attr === LIGHT_MODE) {
    return LIGHT_MODE;
  }

  return DARK_MODE;
}

function resolveThemeColor(mode) {
  if (!canAccessDOM()) {
    return mode === LIGHT_MODE ? "#ffffff" : "#000000";
  }

  try {
    const computed = getComputedStyle(document.documentElement)
      .getPropertyValue("--app-background-color")
      .trim();
    if (computed) {
      return computed;
    }
  } catch (_error) {
    // Intentionally ignore; fall back to static values.
  }

  return mode === LIGHT_MODE ? "#ffffff" : "#000000";
}

export function updateThemeColorMeta(mode = getCurrentMode()) {
  if (!canAccessDOM()) {
    return;
  }

  const color = resolveThemeColor(mode);
  let meta = document.querySelector('meta[name="theme-color"]');

  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "theme-color";
    document.head.appendChild(meta);
  }

  meta.setAttribute("content", color);
}

export function notifyIOSAboutTheme(theme = getCurrentMode(), color) {
  if (!canAccessDOM()) {
    return;
  }

  const normalized = theme === LIGHT_MODE ? LIGHT_MODE : DARK_MODE;
  const payload = {
    type: THEME_CHANGE_EVENT,
    theme: normalized,
    color: color || resolveThemeColor(normalized),
  };

  postToNativeBridge(payload);
}

export function notifyNativeThemeChange(mode = getCurrentMode()) {
  notifyIOSAboutTheme(mode);
}

function deliverToWebkitHandlers(payload) {
  const handlers = window.webkit?.messageHandlers;
  if (!handlers) {
    return false;
  }

  const handler =
    handlers.SuarteAppTheme ||
    handlers.suarteAppTheme ||
    handlers.SuarteApp ||
    handlers.suarteApp ||
    handlers.themeBridge;

  if (handler && typeof handler.postMessage === "function") {
    try {
      handler.postMessage(payload);
      return true;
    } catch (_error) {
      return false;
    }
  }

  return false;
}

function postToNativeBridge(payload) {
  const env = getSuarteEnvironment();
  const hasReactNativeBridge = Boolean(window.ReactNativeWebView?.postMessage);
  const hasWebkitBridge = Boolean(window.webkit?.messageHandlers);
  const hasCustomBridge = Boolean(window.SuarteApp?.postMessage || window.suarteApp?.postMessage);

  if (!env.isNativeWrapper && !hasReactNativeBridge && !hasWebkitBridge && !hasCustomBridge) {
    return;
  }

  const serializedPayload = JSON.stringify(payload);
  let delivered = false;

  try {
    if (hasReactNativeBridge) {
      window.ReactNativeWebView.postMessage(serializedPayload);
      delivered = true;
    }
  } catch (_error) {
    // Ignore bridge errors and try other handlers.
  }

  if (!delivered && hasWebkitBridge) {
    delivered = deliverToWebkitHandlers(payload);
  }

  if (!delivered && hasCustomBridge) {
    const customBridge = window.SuarteApp?.postMessage
      ? window.SuarteApp
      : window.suarteApp;

    if (customBridge && typeof customBridge.postMessage === "function") {
      try {
        customBridge.postMessage(payload);
        delivered = true;
      } catch (_error) {
        // Ignore.
      }
    }
  }

  if (!delivered && typeof window?.dispatchEvent === "function") {
    try {
      window.dispatchEvent(new CustomEvent(THEME_EVENT_NAME, { detail: payload }));
    } catch (_error) {
      // No-op.
    }
  }
}

export function syncStatusBarsWithMode(mode = getCurrentMode()) {
  const normalized = mode === LIGHT_MODE ? LIGHT_MODE : DARK_MODE;
  updateThemeColorMeta(normalized);
  notifyNativeThemeChange(normalized);
}

function watchModeAttribute() {
  if (!canAccessDOM() || typeof MutationObserver === "undefined") {
    return;
  }

  if (window.__suarteThemeBridgeInitialized) {
    return;
  }

  window.__suarteThemeBridgeInitialized = true;

  let lastMode = getCurrentMode();
  syncStatusBarsWithMode(lastMode);

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type !== "attributes" || mutation.attributeName !== "data-mode") {
        continue;
      }

      const nextMode = getCurrentMode();
      if (nextMode === lastMode) {
        continue;
      }

      lastMode = nextMode;
      syncStatusBarsWithMode(nextMode);
    }
  });

  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["data-mode"],
  });
}

watchModeAttribute();

export default {
  getCurrentMode,
  updateThemeColorMeta,
  notifyIOSAboutTheme,
  notifyNativeThemeChange,
  syncStatusBarsWithMode,
};
